import { type ReactNode, useState, useCallback } from 'react';
import { Modal } from '@/components/ui/modal';
import { Frame, ZoomSlider, Footer } from './crop-modal-parts';
import { useCropState } from './use-crop-state';
import { renderCroppedBlob } from './crop-canvas';

type Props = {
  src: string | null;
  onCancel: () => void;
  onConfirm: (blob: globalThis.Blob) => void;
};

function useConfirm(
  src: string | null,
  pixels: ReturnType<typeof useCropState>['pixels'],
  onConfirm: (b: globalThis.Blob) => void
): { busy: boolean; run: () => void } {
  const [busy, setBusy] = useState(false);
  const run = useCallback(async (): Promise<void> => {
    if (!src || !pixels) return;
    setBusy(true);
    try {
      onConfirm(await renderCroppedBlob(src, pixels));
    } finally {
      setBusy(false);
    }
  }, [src, pixels, onConfirm]);
  return { busy, run: () => void run() };
}

function Body({
  src,
  onCancel,
  onConfirm,
}: {
  src: string;
  onCancel: () => void;
  onConfirm: (b: globalThis.Blob) => void;
}): ReactNode {
  const w = useCropState();
  const c = useConfirm(src, w.pixels, onConfirm);
  return (
    <div className="space-y-md">
      <Frame src={src} w={w} />
      <p className="text-center text-[11px] italic leading-snug text-error">
        Asegurate de que dentro del recuadro se vea tu cara completa y el papel
      </p>
      <ZoomSlider value={w.zoom} min={w.minZoom} onChange={w.setZoom} />
      <Footer onCancel={onCancel} onConfirm={c.run} busy={c.busy} />
    </div>
  );
}

export function CropModal({ src, onCancel, onConfirm }: Props): ReactNode {
  return (
    <Modal open={!!src} onClose={onCancel} title="Recorta tu foto" size="md">
      {src && <Body src={src} onCancel={onCancel} onConfirm={onConfirm} />}
    </Modal>
  );
}
